import { Injectable } from "@angular/core";
import { Inspection } from "../types/Inspection/Inspection";
import { ClientService } from "../services/client.service";
import { MapClientRepository } from "./abstract/map.client.repository";
import { Location } from "../types/Location/Location";
import { Inspections } from "../types/Inspection/Inspections";
import { Locations } from "../types/Location/Locations";

@Injectable({
  providedIn: "root",
})
export class InspectionRepository extends MapClientRepository<Location> {
  constructor(clientService: ClientService) {
    super(clientService, "locations");
  }

  public getAllInspectionsAsArray(): Inspection[] {
    return Object.values(this.getAllInspections());
  }

  public getAllInspections(): Inspections {
    const locations = this.get() || {};
    const locationIDs = Object.keys(locations);
    return locationIDs.reduce((acc: any, locationID: string) => {
      const locationMap = super.getById(locationID)?.inspections || {};
      return { ...acc, ...locationMap };
    }, {});
  }

  public getInspectionsByLocationID(locationID: string): Inspections {
    return super.getById(locationID)?.inspections || {};
  }

  public getInspectionsAsArray(locationID: string): Inspection[] {
    const inspections = super.getById(locationID)?.inspections || {};
    return Object.values(inspections);
  }

  public getFinalizedInspectionsAsArray(locationID: string): Inspection[] {
    const inspections = this.searchInspections(
      ["locationID", "finalized", "synced"],
      [locationID, "1", "0"],
    );
    return Object.values(inspections);
  }

  public groupInspectionsByLocationID(inspections: Inspections): any {
    return Object.values(inspections).reduce((acc: any, inspection: any) => {
      const { locationID } = inspection;
      const location = acc[locationID] || {};
      return {
        ...acc,
        [locationID]: {
          ...location,
          [inspection.inspectionID]: inspection,
        },
      };
    }, {});
  }

  private getUpdatedLocations(
    previousLocations: Locations,
    groupedInspections: { [locationID: string]: Inspections },
  ): Locations {
    const updatedLocations = Object.entries(previousLocations).reduce(
      (acc: any, [locationID, location]: any) => {
        const updatedLocation = {
          ...location,
          inspections: groupedInspections[locationID] || location.inspections,
        };
        return {
          ...acc,
          [locationID]: updatedLocation,
        };
      },
      {},
    );
    return updatedLocations;
  }

  public async saveChangesToClient(
    inspectionsToSave: Inspections,
    selectedInspection?: Inspection,
  ): Promise<Inspections> {
    const groupedInspections =
      this.groupInspectionsByLocationID(inspectionsToSave);
    const previousLocations = this.get();
    const updatedLocations = this.getUpdatedLocations(
      previousLocations,
      groupedInspections,
    );
    const currentClient = await this.clientService.getCurrentClientSafe();
    const newClientData = {
      ...currentClient.clientData,
      locations: updatedLocations,
      selectedInspection:
        selectedInspection || currentClient.clientData.selectedInspection,
    };
    const updatedClient = { ...currentClient, clientData: newClientData };
    await this.clientService.setCurrentClient(updatedClient);
    return inspectionsToSave;
  }

  public async saveChangesToClientByLocation(
    locationID: string,
    inspections: Inspections,
  ): Promise<Inspections> {
    const location = super.getById(locationID);
    const updatedLocation = {
      ...location,
      inspections,
    };
    const res = await super.saveRecord(locationID, updatedLocation);
    return res[locationID].inspections;
  }

  public removeInspection(inspectionID: string): Promise<Inspections> {
    const inspection = this.getInspectionByInspectionID(inspectionID);
    if (!inspection) {
      throw new Error("Inspection not found");
    }
    const locationInspections = this.getInspectionsByLocationID(
      inspection.locationID,
    );
    const { [inspectionID]: toRemove, ...rest } = locationInspections;
    return this.saveChangesToClientByLocation(toRemove.locationID, rest);
  }

  public removeMultipleInspections(
    inspectionIDs: string[],
  ): Promise<Inspections> {
    const inspections = this.getAllInspections();
    const filtered = Object.entries(inspections).filter(
      ([, inspection]: [string, Inspection]) => {
        return !inspectionIDs.includes(inspection.inspectionID);
      },
    );
    return this.saveChangesToClient(Object.fromEntries(filtered));
  }

  public saveInspection(inspection: Inspection): Promise<Inspections> {
    const inspections = this.getAllInspections();
    return this.saveChangesToClient(
      {
        ...inspections,
        [inspection.inspectionID]: inspection,
      },
      inspection,
    );
  }

  public searchInspections(keys: string[], values: any): any {
    const inspections: any = this.getAllInspections();
    return Object.fromEntries(
      Object.entries(inspections).filter(([, inspection]: any) => {
        return keys.every((key: string, i: number) => {
          return inspection[key] === values[i];
        });
      }),
    );
  }

  public getInspectionByInspectionID(
    inspectionID: string,
  ): Inspection | undefined {
    const inspections = this.getAllInspectionsAsArray();
    return inspections.find(
      (inspection: Inspection) => inspection.inspectionID === inspectionID,
    );
  }
}
